/**
 * v2 ONU Lookup — search a serial across all chassis and surface its
 * lifecycle history.
 *
 * Same backend (``GET /api/onus/history?serial=...``) as v1; the
 * difference is v2 chrome + CopyableCell on every identifier so
 * operators can copy a serial / display_id / job_id with one click
 * instead of highlighting.
 */
import { useMutation } from "@tanstack/react-query";
import { Link as LinkIcon, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { HelpButton } from "../../help/HelpPanel";

type OnuHistoryEvent = {
  observed_at: string;
  chassis_id: number | null;
  gigafy_device_name: string | null;
  port: string | null;
  display_id: string | null;
  event_type: string;
  detail: Record<string, unknown> | null;
  job_id: string | null;
};

type OnuHistoryResponse = {
  serial: string;
  events: OnuHistoryEvent[];
};

const EVENT_TONE: Record<string, BadgeTone> = {
  first_seen: "success",
  reprovisioned: "success",
  disappeared: "warning",
  forgotten: "danger",
  bulk_cleared: "danger",
  deprovisioned: "danger",
  pending_return: "info",
  moved: "info",
  swapped: "info",
  vnet_replaced: "neutral",
  service_added: "success",
};

export function OnuLookup() {
  const [serial, setSerial] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: async (s: string) => {
      const res = await api.get<OnuHistoryResponse>(
        `/onus/history?serial=${encodeURIComponent(s)}`,
      );
      return res.data;
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const s = serial.trim().toUpperCase();
    if (!s) return;
    setSubmitted(s);
    lookup.mutate(s);
  };

  return (
    <section className="flex flex-col gap-4">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">ONU Lookup</h1>
          <HelpButton topic="tools-onu-lookup" />
        </div>
        <p className="text-xs text-slate-500">
          Search a serial number to see the full lifecycle (first-seen,
          reprovisioned, moved, swapped, deprovisioned, vnet-replaced)
          across every chassis in the fleet.
        </p>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 shadow-border p-3"
      >
        <div className="relative flex-1 min-w-[20rem]">
          <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="e.g. ALCL:48434C00012345"
            className="w-full rounded border border-slate-700 bg-slate-950 py-1.5 pl-7 pr-2 text-sm font-mono text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={lookup.isPending || !serial.trim()}
          className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700 disabled:opacity-50"
        >
          {lookup.isPending ? "Searching…" : "Search"}
        </button>
      </form>

      {submitted && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">
            Lifecycle for <CopyableCell value={submitted} />
          </h2>
          {lookup.isPending ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : lookup.isError ? (
            <p className="text-xs text-red-300">
              Lookup failed — try a different serial.
            </p>
          ) : (lookup.data?.events ?? []).length === 0 ? (
            <p className="text-xs text-slate-500">
              No lifecycle events found for this serial.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1">When</th>
                  <th className="px-2 py-1">Event</th>
                  <th className="px-2 py-1">Chassis</th>
                  <th className="px-2 py-1">Port/Display ID</th>
                  <th className="px-2 py-1">Job</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40 text-slate-300">
                {lookup.data!.events.map((ev, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                      <LocalTime iso={ev.observed_at} />
                    </td>
                    <td className="px-2 py-1">
                      <Badge tone={EVENT_TONE[ev.event_type] ?? "neutral"}>
                        {ev.event_type}
                      </Badge>
                    </td>
                    <td className="px-2 py-1">
                      {ev.chassis_id != null ? (
                        <Link
                          to={`/chassis/${ev.chassis_id}`}
                          className="text-emerald-400 hover:text-emerald-300"
                        >
                          {ev.gigafy_device_name ?? `chassis-${ev.chassis_id}`}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-1 font-mono">
                      {ev.port && ev.display_id ? (
                        <>
                          <CopyableCell value={ev.port} display={ev.port} />/
                          <CopyableCell value={ev.display_id} display={ev.display_id} />
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {ev.job_id ? (
                        <Link
                          to={`/settings/jobs/${ev.job_id}`}
                          className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                        >
                          <LinkIcon className="h-3 w-3" />
                          job
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  );
}
